// Calls to the backends through the dev server's /api/<service>/ proxy, with the session's access token and one
// automatic refresh on 401. Errors follow the contract: {"error": {"code", "message"}}.

import { CREATOR_AGE, CREATOR_AGE_ERROR } from "./creator-age.ts";

export type Service = "accounts" | "social" | "money" | "community" | "deploy" | "rooms" | "store";

export const API_BASE: Record<Service, string> = {
  accounts: "/api/accounts/v1",
  social: "/api/social/v1",
  money: "/api/money/v1",
  community: "/api/community/v1",
  deploy: "/api/deploy/v1",
  rooms: "/api/rooms/v1",
  store: "/api/store/v1",
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

export interface Tokens {
  accessToken: string;
  accessExpiresAt: number;
  refreshToken: string;
  refreshExpiresAt: number;
}

const KEY = "thrdm.session";

export function loadTokens(): Tokens | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  } catch {
    return null;
  }
}

export function saveTokens(t: Tokens | null): void {
  try {
    if (t) localStorage.setItem(KEY, JSON.stringify(t));
    else localStorage.removeItem(KEY);
  } catch {
    /* private mode: the session lasts this page only */
  }
}

let refreshing: Promise<boolean> | null = null;

async function refresh(): Promise<boolean> {
  const t = loadTokens();
  if (!t) return false;
  refreshing ??= (async () => {
    try {
      const res = await fetch(`${API_BASE.accounts}/auth/refresh`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ refreshToken: t.refreshToken }),
      });
      if (!res.ok) {
        saveTokens(null);
        return false;
      }
      const body = (await res.json()) as Tokens;
      saveTokens({ accessToken: body.accessToken, accessExpiresAt: body.accessExpiresAt, refreshToken: body.refreshToken, refreshExpiresAt: body.refreshExpiresAt });
      return true;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/** The current access token, refreshed first when it's about to expire (for the rooms socket, which takes it as a message). */
export async function accessToken(): Promise<string | null> {
  const t = loadTokens();
  if (!t) return null;
  if (t.accessExpiresAt * 1000 < Date.now() + 60_000) await refresh();
  return loadTokens()?.accessToken ?? null;
}

export interface CallOpts {
  method?: string;
  body?: unknown;
  /** send the session token (default: when signed in) */
  auth?: boolean;
  /** money needs an Idempotency-Key on every POST/PUT */
  idempotent?: boolean;
  raw?: boolean;
}

export async function api<T = unknown>(service: Service, path: string, opts: CallOpts = {}): Promise<T> {
  const doFetch = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (opts.body !== undefined) headers["content-type"] = "application/json";
    const t = loadTokens();
    if (t && opts.auth !== false) {
      // refresh a minute before expiry rather than eating a 401
      if (t.accessExpiresAt * 1000 < Date.now() + 60_000) await refresh();
      const fresh = loadTokens();
      if (fresh) headers.authorization = `Bearer ${fresh.accessToken}`;
    }
    if (opts.idempotent) headers["idempotency-key"] = `web:${crypto.randomUUID()}`;
    return fetch(API_BASE[service] + path, {
      method: opts.method ?? (opts.body !== undefined ? "POST" : "GET"),
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  };
  let res = await doFetch();
  if (res.status === 401 && loadTokens() && opts.auth !== false && (await refresh())) res = await doFetch();
  // creator_age with a token from before the 18th birthday: a fresh token carries the new band, so retry once
  else if (res.status === 403 && loadTokens() && opts.auth !== false && (await isCreatorAge(res)) && (await refresh())) res = await doFetch();
  if (opts.raw) return res as unknown as T;
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!res.ok) {
    const err = (body as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
    const code = err?.code ?? `http_${res.status}`;
    // the session was revoked (signed out elsewhere, account deleted): drop it, the page asks to sign in again
    if (res.status === 401 && code === "session_revoked") saveTokens(null);
    throw new ApiError(res.status, code, friendly(code) ?? err?.message ?? `${service} answered ${res.status}`, err?.details);
  }
  return body as T;
}

async function isCreatorAge(res: Response): Promise<boolean> {
  const body = (await res.clone().json().catch(() => null)) as { error?: { code?: string } } | null;
  return body?.error?.code === CREATOR_AGE;
}

/** Plain words for errors every page can hit. */
export function friendly(code: string): string | null {
  if (code === CREATOR_AGE) return CREATOR_AGE_ERROR;
  if (code === "session_revoked") return "Your session has ended. Please sign in again.";
  if (code === "accounts_unreachable") return "We couldn't reach your account just now. Please try again in a moment.";
  return null;
}

/** True when the error means "sign in again" (the page offers the sign-in link). */
export const needsSignIn = (e: unknown): boolean => e instanceof ApiError && e.status === 401 && (e.code === "session_revoked" || e.code === "unauthorized");

/** True for "this endpoint doesn't exist (yet)": a 404/405 without a contract error code from the route itself. */
export function isMissingEndpoint(e: unknown): boolean {
  return e instanceof ApiError && (e.status === 405 || (e.status === 404 && (e.code === "not_found" || e.code === "http_404")));
}
